import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, Trash2, Plus, ArrowRight, Check, ChevronLeft } from "lucide-react";

export default function TailorMadeConvert() {
  const { id } = useParams();
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const nav = useNavigate();

  const [req, setReq] = useState(null);
  const [areas, setAreas] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [step, setStep] = useState(1);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({
    title: "", slug: "", code: "",
    area_id: "", format: "programa_custom", duration_text: "40 horas",
    modality: "blended", target_audience: "", description: "",
    cover_image_url: "", is_published: false, target_tenant_id: "",
    generate_licenses: 0,
  });

  useEffect(() => {
    api.get(`/admin/tailor-made-requests/${id}`).then((r) => {
      const d = r.data;
      setReq(d);
      setForm((f) => ({
        ...f,
        title: d.program_name,
        target_audience: d.target_audience || "",
        modality: d.modality || "blended",
        duration_text: `${d.total_hours_estimated || 40} horas`,
      }));
      const initModules = (d.selected_programs || []).map((p, i) => ({
        id: `m-${i}-${p.id}`,
        title: p.title,
        description: p.description || "",
        estimated_hours: 4,
        lessons: [
          { id: `l-${i}-1`, title: "Introducción", type: "text",
            payload: { markdown: `# ${p.title}\n\n${p.description || ""}`, reading_time_min: 5 },
            duration_min: 5 },
        ],
      }));
      setModules(initModules);
    }).catch(() => toast.error("Request no encontrado"));

    api.get("/catalog/areas").then((r) => setAreas(r.data));
    api.get("/admin/partners").then((r) => setTenants(r.data));
  }, [id]);

  const setStatus = async (status) => {
    try {
      await api.put(`/admin/tailor-made-requests/${id}/status`, { status });
      toast.success(`Marcado como ${status}`);
      setReq({ ...req, status });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publish = async () => {
    try {
      const body = {
        ...form,
        target_tenant_id: form.target_tenant_id || null,
        cover_image_url: form.cover_image_url || null,
        slug: form.slug || null,
        generate_licenses: parseInt(form.generate_licenses, 10) || 0,
        modules: modules.map((m) => ({
          title: m.title, description: m.description,
          estimated_hours: Number(m.estimated_hours) || 4,
          lessons: (m.lessons || []).map((l) => ({
            title: l.title, type: l.type, payload: l.payload, duration_min: Number(l.duration_min) || 0,
          })),
        })),
      };
      const { data } = await api.post(`/admin/programs/from-tailor-made/${id}`, body);
      toast.success("Programa publicado");
      // eslint-disable-next-line no-console
      console.info("[convert] result", data);
      nav("/app/staff/tailor-made");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <Link to="/app/staff/tailor-made" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
          <ChevronLeft className="w-3 h-3" /> Volver a Tailor-Made
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Tailor-Made · Convertir</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold text-white font-display" data-testid="tm-convert-title">
              {req?.program_name || "Cargando..."}
            </h1>
          </div>
          {req && (
            <div className="flex flex-wrap gap-2">
              {req.status !== "contacted" && (
                <button onClick={() => setStatus("contacted")} data-testid="tm-mark-contacted"
                  className="text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-200 hover:bg-white/5">
                  Marcar contactado
                </button>
              )}
              {req.status !== "archived" && (
                <button onClick={() => setStatus("archived")} data-testid="tm-mark-archived"
                  className="text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-200 hover:bg-white/5">
                  Archivar
                </button>
              )}
            </div>
          )}
        </div>

        {req && (
          <section className="mt-6 p-4 bg-[#111827] border border-gray-800 rounded-xl grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Contacto</p>
              <p className="mt-1 text-white">{req.contact?.name} · {req.contact?.email}</p>
              <p className="text-xs text-gray-400">{req.contact?.company} · {req.contact?.phone || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Audiencia</p>
              <p className="mt-1 text-gray-300 text-xs">{req.target_audience}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Duración solicitada</p>
              <p className="mt-1 font-mono text-white">{req.desired_duration} · {req.modality}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Programas base seleccionados</p>
              <p className="mt-1 font-mono text-white">{(req.selected_program_ids || []).length} programas</p>
            </div>
          </section>
        )}

        {req?.brief && (
          <details className="mt-4 p-4 bg-[#0B0F14] border border-gray-800 rounded-xl" data-testid="expanded-brief">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-[#F26A21] font-semibold">
              Brief expandido de IA (5 secciones)
            </summary>
            <div className="mt-3 space-y-3 text-xs text-gray-300">
              <BriefBlock title="A · KYC" data={req.brief.kyc} />
              <BriefBlock title="B · Estructura" data={req.brief.structure} />
              <BriefBlock title="C · Logística" data={req.brief.logistics} />
              <BriefBlock title="D · Contenido" data={req.brief.content} />
              <BriefBlock title="E · Operaciones" data={req.brief.operations} />
              {req.brief.overall_reasoning && (
                <div>
                  <p className="text-[#F26A21] font-semibold">Razonamiento general</p>
                  <p className="text-gray-300 italic">{req.brief.overall_reasoning}</p>
                </div>
              )}
            </div>
          </details>
        )}

        <Stepper step={step} />

        {step === 1 && <StepOne form={form} setForm={setForm} areas={areas} onNext={() => setStep(2)} />}
        {step === 2 && (
          <StepTwo
            modules={modules}
            setModules={setModules}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <StepThree
            form={form} setForm={setForm} tenants={tenants} modulesCount={modules.length}
            onBack={() => setStep(2)} onSubmit={publish}
          />
        )}
      </div>
    </StaffLayout>
  );
}

function Stepper({ step }) {
  return (
    <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-widest">
      {[
        { n: 1, l: "Programa" },
        { n: 2, l: "Módulos" },
        { n: 3, l: "Publicar" },
      ].map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`flex items-center gap-2 ${step === s.n ? "text-[#F26A21]" : step > s.n ? "text-emerald-400" : "text-gray-500"}`}>
            <span className={`w-6 h-6 flex items-center justify-center rounded-full border font-mono ${
              step > s.n ? "bg-emerald-500/10 border-emerald-500/40" :
              step === s.n ? "bg-[#F26A21]/10 border-[#F26A21]/40" : "border-gray-700"
            }`}>{step > s.n ? <Check className="w-3 h-3" /> : s.n}</span>
            <span className="font-semibold">{s.l}</span>
          </div>
          {i < 2 && <div className={`h-px flex-1 ${step > s.n ? "bg-emerald-500/40" : "bg-gray-800"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function StepOne({ form, setForm, areas, onNext }) {
  const go = (e) => {
    e.preventDefault();
    onNext();
  };
  return (
    <form onSubmit={go} className="mt-6 space-y-4" data-testid="tm-step-1">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Título final" val={form.title} on={(v) => setForm({ ...form, title: v, slug: slugify(v) })} req testid="tm-title" />
        <Field label="Slug (auto)" val={form.slug} on={(v) => setForm({ ...form, slug: slugify(v) })} testid="tm-slug" />
        <Field label="Código único" val={form.code} on={(v) => setForm({ ...form, code: v.toUpperCase() })} req testid="tm-code" />
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Área principal *</label>
          <select required value={form.area_id} onChange={(e) => setForm({ ...form, area_id: e.target.value })}
            data-testid="tm-area"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
            <option value="">Selecciona...</option>
            {areas.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
          </select>
        </div>
        <Field label="Duración textual" val={form.duration_text} on={(v) => setForm({ ...form, duration_text: v })} testid="tm-duration" />
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Modalidad</label>
          <select value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })}
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
            {["online", "presencial", "blended", "mixto"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-gray-400">Audiencia objetivo</label>
        <textarea rows={2} value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
          data-testid="tm-audience"
          className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-gray-400">Descripción larga</label>
        <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="tm-desc"
          className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
      </div>
      <Field label="Cover image URL (opcional)" val={form.cover_image_url} on={(v) => setForm({ ...form, cover_image_url: v })} testid="tm-cover" />
      <div className="flex justify-end">
        <button type="submit" data-testid="tm-step1-next"
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold">
          Continuar <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function StepTwo({ modules, setModules, onBack, onNext }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIx = modules.findIndex((m) => m.id === active.id);
    const newIx = modules.findIndex((m) => m.id === over.id);
    setModules(arrayMove(modules, oldIx, newIx));
  };

  const addModule = () => {
    setModules([...modules, {
      id: `m-new-${Date.now()}`, title: "Nuevo módulo", description: "",
      estimated_hours: 4, lessons: [],
    }]);
  };
  const removeModule = (mid) => setModules(modules.filter((m) => m.id !== mid));
  const updateModule = (mid, patch) => setModules(modules.map((m) => m.id === mid ? { ...m, ...patch } : m));
  const addLesson = (mid) => updateModule(mid, {
    lessons: [
      ...(modules.find((m) => m.id === mid)?.lessons || []),
      { id: `l-${Date.now()}`, title: "Nueva lección", type: "text", payload: { markdown: "Contenido", reading_time_min: 5 }, duration_min: 5 },
    ],
  });
  const removeLesson = (mid, lid) => {
    const m = modules.find((x) => x.id === mid);
    updateModule(mid, { lessons: (m.lessons || []).filter((l) => l.id !== lid) });
  };
  const updateLesson = (mid, lid, patch) => {
    const m = modules.find((x) => x.id === mid);
    updateModule(mid, { lessons: (m.lessons || []).map((l) => l.id === lid ? { ...l, ...patch } : l) });
  };

  return (
    <div className="mt-6 space-y-4" data-testid="tm-step-2">
      <p className="text-sm text-gray-400">Arrastra módulos para reordenar. Cada programa seleccionado del brief ya está pre-cargado como módulo.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={modules.map((m) => m.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {modules.map((m) => (
              <SortableModule
                key={m.id} m={m}
                onRemove={() => removeModule(m.id)}
                onUpdate={(patch) => updateModule(m.id, patch)}
                onAddLesson={() => addLesson(m.id)}
                onRemoveLesson={(lid) => removeLesson(m.id, lid)}
                onUpdateLesson={(lid, patch) => updateLesson(m.id, lid, patch)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button onClick={addModule} data-testid="tm-add-module"
        className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-md border border-dashed border-gray-700 text-gray-200 hover:bg-white/5">
        <Plus className="w-4 h-4" /> Agregar módulo
      </button>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-white">← Regresar</button>
        <button onClick={onNext} data-testid="tm-step2-next" disabled={modules.length === 0}
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-40">
          Continuar <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SortableModule({ m, onRemove, onUpdate, onAddLesson, onRemoveLesson, onUpdateLesson }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`tm-module-${m.id}`}
      className="p-4 bg-[#111827] border border-gray-800 rounded-lg">
      <div className="flex items-start gap-2">
        <button {...attributes} {...listeners} aria-label="Reordenar" className="mt-1 text-gray-500 cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 space-y-2">
          <input value={m.title} onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full bg-transparent border-b border-gray-800 text-white font-semibold focus:outline-none focus:border-[#F26A21]"
            data-testid={`tm-module-title-${m.id}`} />
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min={1} value={m.estimated_hours}
              onChange={(e) => onUpdate({ estimated_hours: e.target.value })}
              placeholder="Horas estimadas"
              className="bg-[#0B0F14] border border-gray-800 rounded px-2 py-1 text-xs text-white" />
            <input value={m.description} onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder="Descripción corta"
              className="bg-[#0B0F14] border border-gray-800 rounded px-2 py-1 text-xs text-white" />
          </div>
          {(m.lessons || []).map((l) => (
            <div key={l.id} className="flex items-center gap-2 pl-4 border-l border-gray-800" data-testid={`tm-lesson-${l.id}`}>
              <select value={l.type} onChange={(e) => onUpdateLesson(l.id, { type: e.target.value })}
                className="bg-[#0B0F14] border border-gray-800 rounded px-2 py-1 text-xs text-white">
                {["text", "video", "quiz", "live"].map((t) => (<option key={t}>{t}</option>))}
              </select>
              <input value={l.title} onChange={(e) => onUpdateLesson(l.id, { title: e.target.value })}
                className="flex-1 bg-[#0B0F14] border border-gray-800 rounded px-2 py-1 text-xs text-white" />
              <input type="number" min={0} value={l.duration_min || 0}
                onChange={(e) => onUpdateLesson(l.id, { duration_min: e.target.value })}
                className="w-16 bg-[#0B0F14] border border-gray-800 rounded px-2 py-1 text-xs text-white" />
              <button onClick={() => onRemoveLesson(l.id)} aria-label="Eliminar lección" className="text-red-400">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button onClick={onAddLesson} className="inline-flex items-center gap-1 text-xs text-[#93C5FD] hover:text-white ml-4">
            <Plus className="w-3 h-3" /> Añadir lección
          </button>
        </div>
        <button onClick={onRemove} aria-label="Eliminar módulo" className="text-red-400">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function StepThree({ form, setForm, tenants, modulesCount, onBack, onSubmit }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="mt-6 space-y-4" data-testid="tm-step-3">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Tenant destino</label>
          <select value={form.target_tenant_id} onChange={(e) => setForm({ ...form, target_tenant_id: e.target.value })}
            data-testid="tm-target-tenant"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
            <option value="">Solo catálogo DCC público</option>
            {tenants.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Licencias iniciales a generar</label>
          <input type="number" min={0} value={form.generate_licenses}
            onChange={(e) => setForm({ ...form, generate_licenses: parseInt(e.target.value, 10) || 0 })}
            data-testid="tm-generate-licenses"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
          <p className="mt-1 text-xs text-gray-500">Solo aplica si eliges tenant partner.</p>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
          data-testid="tm-publish"
          className="accent-[#F26A21]" />
        Publicar inmediatamente en catálogo
      </label>
      <div className="p-4 bg-[#0B0F14] border border-gray-800 rounded-lg text-sm text-gray-300">
        <p><span className="text-white font-semibold">{modulesCount}</span> módulos serán creados.</p>
        <p>Programa: <span className="text-white">{form.title}</span> · código <span className="font-mono text-white">{form.code || "—"}</span></p>
        <p>Destino: <span className="text-white">{form.target_tenant_id ? tenants.find((t) => t.id === form.target_tenant_id)?.name : "DCC público"}</span></p>
      </div>
      <label className="flex items-center gap-2 text-sm text-amber-300">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
          data-testid="tm-confirm" />
        Confirmo revisión y quiero publicar
      </label>
      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-white">← Regresar</button>
        <button onClick={onSubmit} disabled={!confirm || !form.code || !form.area_id} data-testid="tm-publish-btn"
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-40">
          Publicar programa <Check className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function Field({ label, val, on, req, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-gray-400">{label}{req && " *"}</span>
      <input required={req} value={val || ""} onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}

function slugify(text) {
  return (text || "").toLowerCase()
    .replace(/[áàäâã]/g, "a").replace(/[éèëê]/g, "e").replace(/[íìïî]/g, "i")
    .replace(/[óòöôõ]/g, "o").replace(/[úùüû]/g, "u").replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function BriefBlock({ title, data }) {
  if (!data) return null;
  return (
    <div className="border-t border-gray-800 pt-2">
      <p className="text-[#F26A21] font-semibold mb-1">{title}</p>
      <pre className="whitespace-pre-wrap font-mono text-[10px] text-gray-300 leading-relaxed">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
